import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNexusRefresh } from '../../context/NexusSyncContext';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { apiService } from "../../services/api";
import OrderRow from "./OrderRow";

export default function BillingBatchManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [batches, setBatches] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewBatch, setShowNewBatch] = useState(false);
  const [newBatch, setNewBatch] = useState({ reference: "", name: "", client_id: "" });
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const showError = useCallback((message) => {
    setError(message);
    window.setTimeout(() => setError(null), 5000);
  }, []);

  const loadClients = useCallback(async () => {
    try {
      setClients(await apiService.get("/clients"));
    } catch {
      setClients([]);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.get("/billing-batches?page=1&size=500");
      setBatches(data.items || []);
    } catch (err) {
      setBatches([]);
      showError(err?.message || "Erreur chargement commandes");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const loadBatchDetails = useCallback(
    async (id) => {
      if (!id) return null;
      setLoadingDetail(true);
      try {
        const detail = await apiService.get(`/billing-batches/${id}`);
        setBatchDetails(detail);
        return detail;
      } catch (err) {
        showError(err?.message || "Commande introuvable");
        setBatchDetails(null);
        return null;
      } finally {
        setLoadingDetail(false);
      }
    },
    [showError],
  );

  useEffect(() => {
    loadClients();
    loadBatches();
  }, [loadClients, loadBatches]);

  useEffect(() => {
    const batchId = searchParams.get("batch");
    if (!batchId) return;
    const idNum = Number(batchId);
    if (idNum === expandedBatchId) return;
    setExpandedBatchId(idNum);
    loadBatchDetails(idNum);
  }, [searchParams, expandedBatchId, loadBatchDetails]);

  const filteredBatches = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return batches;
    return batches.filter((batch) =>
      [batch.reference, batch.name, batch.client_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [batches, searchTerm]);

  const refresh = useCallback(async () => {
    await loadBatches();
    if (expandedBatchId) await loadBatchDetails(expandedBatchId);
  }, [loadBatches, loadBatchDetails, expandedBatchId]);

  useNexusRefresh(['billing', 'orders', 'invoices', 'billing_batches'], refresh);

  const runApi = useCallback(
    async (fn, errorMessage) => {
      try {
        await fn();
        await refresh();
        return true;
      } catch (err) {
        showError(err?.message || errorMessage);
        return false;
      }
    },
    [refresh, showError],
  );

  const handleToggle = useCallback(
    (id) => {
      if (expandedBatchId === id) {
        setExpandedBatchId(null);
        setBatchDetails(null);
        navigate("/billing-batches", { replace: true });
        return;
      }
      setExpandedBatchId(id);
      setBatchDetails(null);
      navigate(`/billing-batches?batch=${id}`, { replace: true });
      loadBatchDetails(id);
    },
    [expandedBatchId, navigate, loadBatchDetails],
  );

  const handleCreateBatch = async (event) => {
    event.preventDefault();
    if (!newBatch.reference.trim()) {
      showError("Reference commande obligatoire");
      return;
    }
    try {
      const created = await apiService.post("/billing-batches", {
        reference: newBatch.reference.trim(),
        name: newBatch.name.trim() || null,
        client_id: newBatch.client_id ? Number(newBatch.client_id) : null,
      });
      setShowNewBatch(false);
      setNewBatch({ reference: "", name: "", client_id: "" });
      await loadBatches();
      setExpandedBatchId(created.id);
      await loadBatchDetails(created.id);
      navigate(`/billing-batches?batch=${created.id}`, { replace: true });
    } catch (err) {
      showError(err?.message || "Creation commande impossible");
    }
  };

  const panelProps = useMemo(
    () => ({
      clients,
      showError,
      onPatchBatch: (patch) =>
        runApi(() => apiService.patch(`/billing-batches/${expandedBatchId}`, patch), "Mise a jour commande impossible"),
      onDeleteBatch: async () => {
        if (!window.confirm("Supprimer cette commande ?")) return false;
        try {
          await apiService.delete(`/billing-batches/${expandedBatchId}`);
          setExpandedBatchId(null);
          setBatchDetails(null);
          await loadBatches();
          navigate("/billing-batches", { replace: true });
          return true;
        } catch (err) {
          showError(err?.message || "Suppression impossible");
          return false;
        }
      },
      onAddLine: (payload) =>
        runApi(() => apiService.post(`/billing-batches/${expandedBatchId}/lines`, payload), "Ajout ligne impossible"),
      onPatchLine: (line, patch) =>
        runApi(() => apiService.patch(`/billing-batches/lines/${line.id}`, patch), "Mise a jour ligne impossible"),
      onDeleteLine: async (line) => {
        if (!window.confirm("Supprimer cette ligne ?")) return false;
        return runApi(() => apiService.delete(`/billing-batches/lines/${line.id}`), "Suppression ligne impossible");
      },
      onCreateInvoice: (payload) =>
        runApi(
          () =>
            apiService.post(`/billing-batches/${expandedBatchId}/cash-events`, {
              ...payload,
              label: payload.label || `Facture ${batchDetails?.reference || expandedBatchId}`,
              amount_eur: 0,
              event_type: "Solde",
              direction: "inflow",
            }),
          "Creation facture impossible",
        ),
      onUpdateInvoice: (invoice, payload) =>
        runApi(() => apiService.patch(`/billing-batches/cash-events/${invoice.id}`, payload), "Mise a jour facture impossible"),
      onUpdateInvoiceBillability: (invoice, billabilityStatus) =>
        runApi(
          () =>
            apiService.patch(`/billing-batches/cash-events/${invoice.id}/billability`, {
              billability_status: billabilityStatus,
            }),
          "Mise a jour facturabilite impossible",
        ),
      onDeleteInvoice: async (invoice) => {
        if (!window.confirm("Supprimer cette facture ?")) return false;
        return runApi(() => apiService.delete(`/billing-batches/cash-events/${invoice.id}`), "Suppression facture impossible");
      },
    }),
    [clients, showError, runApi, expandedBatchId, batchDetails, loadBatches, navigate],
  );

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-6 text-slate-900 sm:px-6">
      <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Facturation</p>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">Commandes et factures</h1>
          </div>
          <button
            type="button"
            onClick={() => setShowNewBatch((value) => !value)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800"
          >
            <Plus className="h-4 w-4" />
            Nouvelle commande
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>
        )}

        {showNewBatch && (
          <form
            onSubmit={handleCreateBatch}
            className="grid gap-2 rounded-xl border border-blue-100 bg-white p-3 shadow-sm sm:grid-cols-2 lg:grid-cols-5"
          >
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder="Reference commande"
              value={newBatch.reference}
              onChange={(event) => setNewBatch({ ...newBatch, reference: event.target.value })}
            />
            <input
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm lg:col-span-2"
              placeholder="Nom / objet"
              value={newBatch.name}
              onChange={(event) => setNewBatch({ ...newBatch, name: event.target.value })}
            />
            <select
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={newBatch.client_id}
              onChange={(event) => setNewBatch({ ...newBatch, client_id: event.target.value })}
            >
              <option value="">Client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <button className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800">
              Creer
            </button>
          </form>
        )}

        <div className="relative max-w-md">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm"
            placeholder="Rechercher une commande"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          {loading && <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Chargement…</div>}
          {!loading &&
            filteredBatches.map((batch) => (
              <OrderRow
                key={batch.id}
                batch={batch}
                expanded={expandedBatchId === batch.id}
                detail={expandedBatchId === batch.id ? batchDetails : null}
                loadingDetail={expandedBatchId === batch.id && loadingDetail}
                onToggle={() => handleToggle(batch.id)}
                panelProps={panelProps}
              />
            ))}
          {!loading && filteredBatches.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
              Aucune commande.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
